/**
 * The tool registry, its grants, and the combinations nobody would notice one tool at a time.
 *
 * The combinations panel is the reason this screen exists rather than being a table on the agent
 * detail page. Tool misuse is the threat where every individual call is authorised, so a per-tool
 * view is structurally incapable of showing it: each row looks fine, and the danger is in the set.
 * Putting combinations first, above the registry, makes the screen answer the question a reviewer
 * cannot answer anywhere else.
 */

import { Link } from "react-router-dom";
import type { IdentityKind } from "@hub/core/browser";
import { relative, useApi } from "../lib/api";
import { Card, KindBadge, Pill } from "../components";

interface ToolRow {
  id: string; name: string; description: string; classes: string[]; risk: string;
  source: string; pinnedVersion: string | null; autoUpdates: boolean;
  reviewedBy: string | null; reviewedAt: string | null; minimumTier: number;
}
interface GrantRow {
  id: string; identityId: string; taskRef: string | null; scopeNarrowing: string | null;
  expiresAt: string | null; grantedBy: string; grantedAt: string; standing: boolean;
  identity: { id: string; displayName: string; kind: IdentityKind };
  tool: { id: string; name: string; risk: string; classes: string[] };
}
interface ComboRow {
  identityId: string; displayName: string; kind: IdentityKind;
  findings: { rule: { id: string; severity: "review" | "block"; concern: string; mitigation: string }; tools: string[] }[];
}

const RISK_TONE: Record<string, "ok" | "warn" | "bad" | "dim"> = {
  low: "dim", moderate: "ok", high: "warn", critical: "bad",
};

export function Tools() {
  const { data, error, loading } = useApi<{ tools: ToolRow[]; grants: GrantRow[]; combinations: ComboRow[] }>("/v1/tools");

  if (loading) return <p className="empty">Loading…</p>;
  if (error) return <p className="err">{error.message}{error.hint ? ` — ${error.hint}` : ""}</p>;
  if (!data) return null;

  const unpinned = data.tools.filter(
    (t) => t.pinnedVersion === null || t.pinnedVersion.toLowerCase() === "latest");
  const auto = data.tools.filter((t) => t.autoUpdates);
  const unreviewed = data.tools.filter((t) => t.reviewedBy === null);
  const standing = data.grants.filter((g) => g.standing && g.tool.risk !== "low");
  const blocks = data.combinations.filter((c) => c.findings.some((f) => f.rule.severity === "block"));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Tools</h1>
          <p className="sub">
            What an agent can actually call, where the implementation comes from, and which
            combinations are more dangerous than any of their parts. A tool is granted{" "}
            <strong>per task</strong>, not per agent — the same identity gets different permissions
            in different workflows.
          </p>
        </div>
        <Link className="btn" to="/threats">Threat model</Link>
      </div>

      <div className="grid k4" style={{ marginBottom: 14 }}>
        <Stat label="Registered tools" n={data.tools.length} tone="dim"
              note={`${data.grants.length} live grants`} />
        <Stat label="Unpinned or 'latest'" n={unpinned.length} tone={unpinned.length ? "bad" : "ok"}
              note="What would run cannot be stated." />
        <Stat label="Auto-updating" n={auto.length} tone={auto.length ? "bad" : "ok"}
              note="The supply-chain vector, by name." />
        <Stat label="Open-ended grants" n={standing.length} tone={standing.length ? "warn" : "ok"}
              note="Above low risk, with no task and no expiry." />
      </div>

      {/* COMBINATIONS FIRST. A per-tool view cannot show this, which is the whole point. */}
      <Card title={`Dangerous combinations (${data.combinations.length})`}>
        {data.combinations.length === 0 && (
          <p className="empty">
            No identity holds a combination that trips a rule. Individual grants are still worth
            reviewing; this panel only sees pairs.
          </p>
        )}
        {blocks.length > 0 && (
          <p style={{ fontSize: 12.5, color: "var(--bad)", marginTop: 0 }}>
            {blocks.length} identity(ies) hold a combination with no acceptable configuration.
            Separation of duties, not a tighter setting.
          </p>
        )}
        <div className="stack">
          {data.combinations.map((c) => (
            <div key={c.identityId} data-testid="combination">
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                <KindBadge kind={c.kind} label={c.displayName} />
                <Link className="btn small" to={`/agents/${c.identityId}`}>Open</Link>
              </div>
              {c.findings.map((f) => (
                <div key={f.rule.id} style={{
                  paddingLeft: 12, marginBottom: 8,
                  borderLeft: `3px solid var(--${f.rule.severity === "block" ? "bad" : "warn"})`,
                }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Pill tone={f.rule.severity === "block" ? "bad" : "warn"}>{f.rule.severity}</Pill>
                    <strong style={{ fontSize: 12.5 }}>{f.tools.join(" + ")}</strong>
                  </div>
                  <div className="sub" style={{ fontSize: 12.5, marginTop: 2 }}>{f.rule.concern}</div>
                  {/* The way forward, always. A refusal with no remedy is one people route around. */}
                  <div style={{ fontSize: 12.5, marginTop: 2, color: "var(--accent)" }}>{f.rule.mitigation}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Card>

      <div style={{ marginTop: 14 }}>
        <Card title="Registry">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Tool</th><th>Classes</th><th>Risk</th>
                  <th className="hide-m">Source</th><th>Pin</th><th className="hide-m">Reviewed</th><th>Min tier</th>
                </tr>
              </thead>
              <tbody>
                {data.tools.map((t) => (
                  <tr key={t.id} data-testid="tool-row">
                    <td>
                      <div>{t.name}</div>
                      <div style={{ color: "var(--muted)", fontSize: 12 }}>{t.description}</div>
                    </td>
                    <td style={{ color: "var(--muted)", fontSize: 12 }}>{t.classes.join(", ")}</td>
                    <td><Pill tone={RISK_TONE[t.risk] ?? "dim"}>{t.risk}</Pill></td>
                    <td className="hide-m" style={{ color: "var(--muted)", fontSize: 12 }}>{t.source}</td>
                    <td>
                      {/* Both supply-chain signals live here, in a column that survives the mobile
                          breakpoint. `auto-updates` was in the Source column, which carries
                          `.hide-m` — so at 390px the single most important finding on this screen
                          was invisible. Caught by the end-to-end suite, not by looking. */}
                      {t.pinnedVersion === null
                        ? <Pill tone="bad">unpinned</Pill>
                        : t.pinnedVersion.toLowerCase() === "latest"
                          ? <Pill tone="bad">“latest”</Pill>
                          : <span style={{ color: "var(--muted)" }}>{t.pinnedVersion}</span>}
                      {t.autoUpdates && <> <Pill tone="bad">auto-updates</Pill></>}
                    </td>
                    <td className="hide-m">
                      {t.reviewedBy
                        ? <span style={{ color: "var(--muted)", fontSize: 12 }}>{t.reviewedBy}</span>
                        : <Pill tone="bad">never</Pill>}
                    </td>
                    <td><span className={`tier t${t.minimumTier}`}>Tier {t.minimumTier}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {unreviewed.length > 0 && (
            <p style={{ fontSize: 12.5, color: "var(--bad)", marginBottom: 0, marginTop: 10 }}>
              {unreviewed.length} tool(s) reached the registry without a recorded reviewer. A tool
              should arrive through a review, not through discovery.
            </p>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title={`Grants (${data.grants.length})`}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Holder</th><th>Tool</th><th>Task</th>
                  <th className="hide-m">Narrowed to</th><th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {data.grants.slice(0, 60).map((g) => (
                  <tr key={g.id} data-testid="grant-row">
                    <td><KindBadge kind={g.identity.kind} label={g.identity.displayName} /></td>
                    <td>
                      {g.tool.name} <Pill tone={RISK_TONE[g.tool.risk] ?? "dim"}>{g.tool.risk}</Pill>
                    </td>
                    <td>
                      {g.taskRef
                        ? <code style={{ fontSize: 12 }}>{g.taskRef}</code>
                        : <Pill tone={g.tool.risk === "low" ? "dim" : "warn"}>no task — standing</Pill>}
                    </td>
                    <td className="hide-m" style={{ color: "var(--muted)", fontSize: 12 }}>
                      {g.scopeNarrowing ?? (g.tool.classes.includes("execute")
                        ? <Pill tone="bad">nothing — the allowlist is the tool</Pill>
                        : "—")}
                    </td>
                    <td style={{ color: "var(--muted)", fontSize: 12 }}>
                      {g.expiresAt ? relative(g.expiresAt) : "never"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.grants.length > 60 && (
            <p className="sub" style={{ fontSize: 12, marginBottom: 0 }}>
              Showing 60 of {data.grants.length}. The counts above are over all of them.
            </p>
          )}
        </Card>
      </div>
    </>
  );
}

function Stat({ label, n, tone, note }: { label: string; n: number; tone: "ok" | "warn" | "bad" | "dim"; note: string }) {
  return (
    <div className="card kpi">
      <div className="label">{label}</div>
      <div className="value" style={{ color: tone === "dim" ? "var(--text)" : `var(--${tone})` }}>{n}</div>
      <div className="delta">{note}</div>
    </div>
  );
}
